// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for modifying asset project assignments.
 * This module provides a comprehensive interface for updating
 * the dates of an existing project assignment, with support
 * for date validation, timeline visualization, and assignment
 * deletion. It ensures assignments maintain chronological order
 * and prevents overlaps.
 *
 * Key features:
 * - Date validation
 * - Timeline visualization
 * - Assignment deletion
 * - Error handling
 * - Real-time updates
 */
import { hasRangeViolationError } from '@/api/error-utils';
import { deleteAssetProjectAssignment } from '@/asset/api/delete-asset-project-assignment';
import {
  AssetProjectHistorySegment,
  getAssetProjectHistory,
} from '@/asset/api/get-asset-project-history';
import { Asset } from '@/asset/api/types';
import { updateAssetProjectAssignment } from '@/asset/api/update-asset-project-assignment';
import {
  AssetProjectHistoryTimeline,
  HISTORY_SEGMENT_INPUT_ID,
} from '@/asset/components/asset-project-history-timeline';
import { FormDatePicker } from '@/components/form-date-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import { normalizeDateTime } from '@/utilities/date-time';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

/**
 * Calculates the valid interval for a project assignment segment.
 * Handles edge cases like future end dates and invalid ranges.
 *
 * @param segment - The segment to calculate interval for
 * @returns A Luxon interval representing the segment's duration
 *
 * @example
 * ```ts
 * const interval = getSegmentInterval({
 *   start: DateTime.fromISO('2024-01-01'),
 *   end: DateTime.fromISO('2024-01-31')
 * });
 * // interval spans Jan 1-31, 2024
 * ```
 */
function getSegmentInterval(segment: AssetProjectHistorySegment) {
  const today = DateTime.now().endOf('day');
  const end = segment.end ?? today;

  if (segment.start > end) {
    return today.until(today);
  }

  return segment.start.until(end);
}

/**
 * Calculates the visible interval for the timeline display.
 * Adds padding days before and after the segment to provide context.
 *
 * @param assignment - The assignment segment to display
 * @returns A Luxon interval for timeline visualization
 *
 * @example
 * ```ts
 * const interval = getTimelineInterval({
 *   start: DateTime.fromISO('2024-01-15'),
 *   end: DateTime.fromISO('2024-01-20')
 * });
 * // interval includes padding days before Jan 15 and after Jan 20
 * ```
 */
function getTimelineInterval(assignment: AssetProjectHistorySegment) {
  const interval = getSegmentInterval(assignment);
  const length = interval.length('days');
  const days = Math.max(length, 7);

  return interval.set({
    start: interval.start.minus({ days }),
    end: DateTime.min(
      interval.end.plus({ days }),
      DateTime.now().endOf('day').plus({ days: 7 })
    ),
  });
}

/**
 * Returns a tuple representing the valid date range for the given assignment.
 * Luxon intervals must always be closed, so this function returns a tuple
 * instead of a Luxon interval. The interval is open on the left if there are
 * no segments before the assignment, and open on the right if there are no
 * segments after the assignment.
 *
 * @param assignment - The assignment to check
 * @param segments - All segments in chronological order
 * @returns Tuple of [earliest allowed start, latest allowed end]
 *
 * @example
 * ```ts
 * const [minStart, maxEnd] = getDateRange(
 *   currentAssignment,
 *   allAssignments
 * );
 * // Returns bounds based on adjacent assignments
 * ```
 */
function getDateRange(
  assignment: AssetProjectHistorySegment,
  segments: AssetProjectHistorySegment[]
): [DateTime | undefined, DateTime | undefined] {
  const index = segments.findIndex(({ id }) => id === assignment.id);

  if (index === -1) {
    return [undefined, undefined];
  }

  let start: DateTime | undefined;
  let end: DateTime | undefined;

  if (index > 0) {
    start = segments[index - 1].end ?? undefined;
  }

  if (index < segments.length - 1) {
    end = segments[index + 1].start;
  }

  return [start, end];
}

/**
 * Schema for validating form input data.
 * Enforces required fields and date format.
 */
const formSchema = z.object({
  start: dateSchema.nullable(),
  end: dateSchema.nullable(),
});

/**
 * Type definition for form data.
 * Used for both input and output validation.
 *
 * @typedef {Object} FormData
 */
type FormData = z.infer<typeof formSchema>;

/**
 * Props for configuring the UpdateAssetAssignmentDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset being updated.
   * Only requires key and serial fields.
   */
  asset: Pick<Asset, 'key' | 'serial'>;

  /**
   * The assignment segment to modify.
   * Must belong to the specified asset.
   */
  assignment: AssetProjectHistorySegment;

  /**
   * If true, the dialog is open and visible.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for updating asset project assignments.
 * Provides a form interface for modifying assignment dates
 * with timeline visualization and validation.
 *
 * Features:
 * - Date range validation
 * - Timeline visualization
 * - Assignment deletion
 * - Form state management
 * - Error message display
 * - Real-time query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView({ asset, assignment }: Props) {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <UpdateAssetAssignmentDialog
 *       asset={asset}
 *       assignment={assignment}
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function UpdateAssetAssignmentDialog(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const deleteConfirmation = useDisclosure();

  const historyQuery = useQuery({
    queryKey: ['asset', props.asset.key, 'project-history'],
    queryFn: () => getAssetProjectHistory(props.asset.key),
  });

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => {
      if (data.start === null) {
        throw new Error('Start date is required');
      }

      const start = normalizeDateTime(data.start);
      const end = data.end === null ? null : normalizeDateTime(data.end);

      return updateAssetProjectAssignment(
        props.assignment.project.key,
        props.assignment.key,
        { start, end }
      );
    },
    onSuccess: (data) => {
      props.onClose();

      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });
      queryClient.invalidateQueries({
        queryKey: ['project', props.assignment.project.key],
      });

      reset({
        start: data.segment.start,
        end: data.segment.end,
      });
    },
    onError: (error) => {
      if (hasRangeViolationError(error)) {
        setError('start', {
          type: 'manual',
          message:
            'An asset assignment already intersects with this date range.',
        });
        setError('end', {
          type: 'manual',
          message:
            'Please adjust the date range or correct the dates for the existing assignment.',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      return deleteAssetProjectAssignment(
        props.assignment.project.key,
        props.assignment.key
      );
    },
    onSuccess: () => {
      close();

      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });
      queryClient.invalidateQueries({
        queryKey: ['project', props.assignment.project.key],
      });
    },
    onError: () => {
      deleteConfirmation.close();
    },
  });

  const history = historyQuery.data?.history ?? [];
  const [minimumStartDate, maximumEndDate] = getDateRange(
    props.assignment,
    history
  );

  const hasNextAssignment = history.some(({ id, start }) => {
    if (props.assignment.end === null) {
      return false;
    }

    if (id === props.assignment.id) {
      return false;
    }

    return start >= props.assignment.end;
  });

  const resolverSchema = formSchema.superRefine((data, context) => {
    if (data.start === null) {
      context.addIssue({
        path: ['start'],
        code: z.ZodIssueCode.custom,
        message: 'Start date is required',
      });
    }

    if (data.end === null && hasNextAssignment) {
      context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'End date is required',
      });
    }

    if (data.start === null || data.end === null) {
      return;
    }

    if (data.start < MINIMUM_DATE) {
      context.addIssue({
        path: ['start'],
        code: z.ZodIssueCode.custom,
        message: 'Start date cannot be before January 1, 1970',
      });
    }

    if (data.end < MINIMUM_DATE) {
      context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'End date cannot be before January 1, 1970',
      });
    }

    if (data.start > data.end) {
      return context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'End date must be after start date',
      });
    }

    const today = DateTime.now().endOf('day');

    if (data.start > today) {
      context.addIssue({
        path: ['start'],
        code: z.ZodIssueCode.custom,
        message: 'Assets cannot be assigned in the future',
      });
    }

    if (data.end > today) {
      context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: 'Assets cannot be assigned in the future',
      });
    }

    if (minimumStartDate !== undefined && data.start < minimumStartDate) {
      context.addIssue({
        path: ['start'],
        code: z.ZodIssueCode.custom,
        message: `Start date must be on or after ${minimumStartDate.toLocaleString({ dateStyle: 'medium' })}`,
      });
    }

    if (maximumEndDate !== undefined && data.end > maximumEndDate) {
      context.addIssue({
        path: ['end'],
        code: z.ZodIssueCode.custom,
        message: `End date must be on or before ${maximumEndDate.toLocaleString({ dateStyle: 'medium' })}`,
      });
    }
  });

  const { control, formState, reset, setError, handleSubmit } =
    useForm<FormData>({
      resolver: zodResolver(resolverSchema),
      defaultValues: {
        start: props.assignment.start,
        end: props.assignment.end,
      },
    });

  const timeout = useTimeout(reset, theme.transitions.duration.standard);
  const start = useWatch({ control, name: 'start' });
  const end = useWatch({ control, name: 'end' });

  const startDateTime = start;
  const endDateTime = end;
  const maximumStartDate = endDateTime ?? maximumEndDate;
  const minimumEndDate = startDateTime ?? minimumStartDate;
  const shouldUpdate = formState.isDirty;
  const isDisabled =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    historyQuery.isPending;

  const timelineInterval = getTimelineInterval(props.assignment);
  const timelineHistory = history.map((segment) => {
    if (segment.id === props.assignment.id) {
      if (start === null) {
        return { ...segment, id: HISTORY_SEGMENT_INPUT_ID };
      }

      if (end === null || end >= start) {
        return {
          ...segment,
          id: HISTORY_SEGMENT_INPUT_ID,
          start,
          end,
        };
      }

      return { ...segment, id: HISTORY_SEGMENT_INPUT_ID };
    }

    return segment;
  });

  const isInvalid = timelineHistory.some((segment, _, segments) => {
    const segmentInterval = getSegmentInterval(segment);

    return segments.some((other) => {
      if (segment.id === other.id) {
        return false;
      }

      const otherInterval = getSegmentInterval(other);

      return segmentInterval.overlaps(otherInterval);
    });
  });

  const close = (event?: unknown) => {
    if (isDisabled) return;

    if (event instanceof Event) {
      event.stopPropagation();
    }

    props.onClose();
    timeout.start();
    deleteConfirmation.close();
  };

  const handleClickDelete = () => {
    deleteConfirmation.open();
  };

  const handleClickConfirm = () => {
    deleteMutation.mutate();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (shouldUpdate) {
      return updateMutation.mutate(data);
    }

    close();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      transitionDuration={theme.transitions.duration.standard}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={
          updateMutation.isError &&
          !hasRangeViolationError(updateMutation.error)
        }
        onClose={updateMutation.reset}
        message="An error occurred while updating the asset assignment."
      />
      <SupportSnackbar
        isOpen={deleteMutation.isError}
        onClose={deleteMutation.reset}
        message="An error occurred while deleting the asset assignment."
      />
      <Dialog
        fullWidth
        maxWidth="sm"
        open={deleteConfirmation.isOpen}
        onClose={deleteConfirmation.close}
      >
        <DialogTitle>Delete assignment</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete this assignment? The data will be
            permanently removed from the asset’s history.
          </Typography>
          <Typography>
            Note: your audit log will continue to show a record of the deleted
            assignment.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={deleteConfirmation.close}>No, go back</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleClickConfirm}
            disabled={deleteMutation.isPending}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
      <DialogTitle>Asset assignment</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2}>
          <Stack flex={1}>
            <Typography variant="subtitle1">Asset</Typography>
            <Typography variant="body1">{props.asset.serial}</Typography>
          </Stack>
          <Stack flex={1}>
            <Typography variant="subtitle1">Project</Typography>
            <Typography variant="body1">
              {props.assignment.project.name}
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={2} marginTop={4} marginBottom={2}>
          <FormDatePicker
            disableFuture
            label="Start date"
            name="start"
            control={control}
            isDisabled={formState.isSubmitting}
            minimumDate={minimumStartDate}
            maximumDate={maximumStartDate}
          />
          <FormDatePicker
            disableFuture
            label="End date"
            name="end"
            control={control}
            isDisabled={formState.isSubmitting}
            maximumDate={maximumEndDate}
            minimumDate={minimumEndDate}
          />
        </Stack>
        <Collapse in={isInvalid}>
          <Alert severity="error">
            This assignment overlaps with another assignment for this asset.
            Please adjust the date range or correct the dates for the existing
            assignment.
          </Alert>
        </Collapse>
      </DialogContent>
      <AssetProjectHistoryTimeline
        asset={props.asset}
        history={timelineHistory}
        interval={timelineInterval}
        maximumHeight={100}
      />
      <Divider />
      <DialogActions>
        <Button
          color="error"
          disabled={isDisabled}
          onClick={handleClickDelete}
          sx={{ marginRight: 'auto' }}
        >
          Delete
        </Button>
        <Button onClick={close} disabled={isDisabled}>
          Cancel
        </Button>
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          disabled={isDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
